import {
    Button,
    Input,
    makeStyles,
    MessageBar,
    MessageBarActions,
    MessageBarBody,
    tokens,
    Title3,
} from '@fluentui/react-components';
import { ArrowDownloadRegular, DismissRegular, SearchRegular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AppState } from '../../AppState';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { AttachmentProvider } from '../../contexts/AttachmentContext';
import { useChat } from '../../libs/hooks/useChat';
import { useChatExport } from '../../libs/hooks/useChatExport';
import { useChatHistory } from '../../libs/hooks/useChatHistory';
import { ChatHistoryList } from '../chat/ChatHistoryList';
import { ChatRoomHeader } from '../chat/ChatRoomHeader';
import { ChatRoomInlineHeader } from '../chat/ChatRoomInlineHeader';
import { ChatSidebar } from '../chat/ChatSidebar';
import { ExportConfirmDialog } from '../chat/ExportConfirmDialog';

// Define styles using makeStyles
const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        border: 'none',
        backgroundColor: tokens.colorNeutralBackground3,
        boxShadow: 'var(--app-shadow-card)',
    },
    mainContainer: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: '100%',
        overflow: 'hidden',
        paddingLeft: '0',
        paddingRight: '0',
        maxWidth: '1200px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        padding: tokens.spacingVerticalL,
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
    },
    headerEmbedded: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        padding: tokens.spacingVerticalS,
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
    },
    search: {
        border: `1px solid ${tokens.colorNeutralForeground3}`,
        borderRadius: '8px',
        backgroundColor: tokens.colorNeutralBackground1,
        boxShadow: 'none',
        height: '36px',
        width: '100%',
        boxSizing: 'border-box',
        transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out',
        '& input::placeholder': {
            color: tokens.colorNeutralForeground2,
        },
        '&::after': {
            display: 'none !important' as never,
            borderRadius: '0 !important' as never,
            borderWidth: '0 !important' as never,
        },
        '&:focus-within::after': {
            display: 'none !important' as never,
            borderWidth: '0 !important' as never,
        },
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground4,
            border: `1px solid ${tokens.colorNeutralStrokeAccessible}`,
        },
        '&:focus-within': {
            backgroundColor: `${tokens.colorNeutralBackground1} !important` as never,
            border: `1px solid ${tokens.colorBrandForeground1} !important` as never,
            boxShadow: 'none !important' as never,
            outline: 'none !important' as never,
            color: tokens.colorNeutralForeground1,
        },
    },
    searchContainer: {
        width: '100%',
        marginTop: tokens.spacingVerticalS,
        paddingLeft: '0',
        paddingRight: '0',
    },
    searchContainerEmbedded: {
        width: '100%',
        marginTop: tokens.spacingVerticalXXS,
        paddingLeft: '0',
        paddingRight: '0',
    },
    historyTitle: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: '20px',
        lineHeight: '100%',
        letterSpacing: '0%',
        verticalAlign: 'middle',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: tokens.spacingHorizontalM,
        marginTop: tokens.spacingVerticalS,
        paddingBottom: tokens.spacingVerticalS,
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    exportButton: {
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },
    exportAlert: {
        marginTop: tokens.spacingVerticalS,
    },
});

// The React Component
export const HistoryPage: React.FC = () => {
    const classes = useClasses();
    const [searchQuery, setSearchQuery] = useState('');
    const { embeddedMode } = useAppSelector((state: RootState) => state.app);
    const featureFlags = useAppSelector((state: RootState) => state.app.serviceInfo.featureFlags);
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    const chat = useChat();
    const navigate = useNavigate();
    const { t } = useTranslation();
    // Single history fetch for the whole page: passed down to ChatHistoryList (so it no longer fetches
    // its own copy — one request per view instead of two) and read here to gate the export button.
    const { history, isLoading: isLoadingHistory, error: historyError, refetch: refetchHistory } = useChatHistory();
    const { exportChats, isExporting, exportAlert, dismissExportAlert } = useChatExport();
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [confirmOpen, setConfirmOpen] = useState(false);
    // Set by ChatHistoryList when "select all" is capped to the export limit; shown as an info bar.
    const [selectionNotice, setSelectionNotice] = useState<string | null>(null);

    const handleConfirmExport = async () => {
        const outcome = await exportChats([...selectedIds]);
        setConfirmOpen(false);
        // Clear the selection only on a real export, so after a rate-limit / error the user can retry
        // the same set without re-picking everything.
        if (outcome.status === 'success') {
            setSelectedIds(new Set());
            setSelectionNotice(null);
        }
    };

    // Unwrapped on purpose: this is reached only via the setAppState interceptor below, which the
    // header's already-guarded onNewChat triggers (both ChatRoomHeader and ChatRoomInlineHeader route
    // through requestNewChat). Wrapping again would double-guard — a second warning dialog, and an
    // orphan chat if the customer cancels it after the header's proceed already created one. The
    // navigate('/') is required or a new chat is created in Redux but the router stays on /history.
    const handleNewChat = async () => {
        try {
            await chat.createChat(hostContext);
            await navigate('/');
        } catch (error) {
            console.error('Failed to create new chat:', error);
        }
    };

    const setAppState: React.Dispatch<React.SetStateAction<AppState>> = (state) => {
        if (state === AppState.SettingUserInfo) {
            void handleNewChat();
        }
    };

    return (
        <AttachmentProvider>
            <div className={classes.root}>
                {!embeddedMode && <ChatRoomHeader setAppState={setAppState} />}
                <ChatRoomInlineHeader setAppState={setAppState} isVisible={embeddedMode} />
                <div className={classes.mainContainer}>
                    <ChatSidebar />
                    <div className={classes.content}>
                        <div className={embeddedMode ? classes.headerEmbedded : classes.header}>
                            <div className={classes.titleRow}>
                                <Title3 className={classes.historyTitle}>{t('sidebar.history')}</Title3>
                                {featureFlags.ChatHistoryExportEnabled && (
                                    <Button
                                        className={classes.exportButton}
                                        appearance="secondary"
                                        icon={<ArrowDownloadRegular />}
                                        disabled={isExporting || isLoadingHistory || selectedIds.size === 0}
                                        onClick={() => {
                                            // Open the confirm dialog; the endpoint is only called on confirm.
                                            setConfirmOpen(true);
                                        }}
                                    >
                                        {isExporting ? t('chatHistory.exporting') : t('chatHistory.export')}
                                    </Button>
                                )}
                            </div>
                            {exportAlert && (
                                <MessageBar
                                    intent={exportAlert.intent}
                                    className={classes.exportAlert}
                                    role={exportAlert.intent === 'error' ? 'alert' : 'status'}
                                >
                                    <MessageBarBody>{exportAlert.message}</MessageBarBody>
                                    <MessageBarActions
                                        containerAction={
                                            <Button
                                                appearance="transparent"
                                                icon={<DismissRegular />}
                                                aria-label={t('alerts.dismissMessageAriaLabel')}
                                                onClick={dismissExportAlert}
                                            />
                                        }
                                    />
                                </MessageBar>
                            )}
                            {selectionNotice && (
                                <MessageBar intent="info" className={classes.exportAlert} role="status">
                                    <MessageBarBody>{selectionNotice}</MessageBarBody>
                                    <MessageBarActions
                                        containerAction={
                                            <Button
                                                appearance="transparent"
                                                icon={<DismissRegular />}
                                                aria-label={t('alerts.dismissMessageAriaLabel')}
                                                onClick={() => {
                                                    setSelectionNotice(null);
                                                }}
                                            />
                                        }
                                    />
                                </MessageBar>
                            )}
                            <div className={embeddedMode ? classes.searchContainerEmbedded : classes.searchContainer}>
                                <Input
                                    className={classes.search}
                                    placeholder={t('datatable.search')}
                                    type="search"
                                    contentBefore={<SearchRegular />}
                                    onChange={(_, data) => {
                                        setSearchQuery(data.value);
                                    }}
                                />
                            </div>
                        </div>
                        <ChatHistoryList
                            searchQuery={searchQuery}
                            history={history}
                            isLoading={isLoadingHistory}
                            historyError={historyError}
                            refetch={refetchHistory}
                            selectable={featureFlags.ChatHistoryExportEnabled}
                            selectedIds={selectedIds}
                            setSelectedIds={setSelectedIds}
                            onSelectionNotice={setSelectionNotice}
                        />
                        {featureFlags.ChatHistoryExportEnabled && (
                            <ExportConfirmDialog
                                open={confirmOpen}
                                count={selectedIds.size}
                                busy={isExporting}
                                onCancel={() => {
                                    setConfirmOpen(false);
                                }}
                                onConfirm={() => {
                                    void handleConfirmExport();
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </AttachmentProvider>
    );
};
